import { useCallback } from "react";
import { type MutationHookOptions } from "@apollo/client";
import { useCreateVaultDocumentMutation } from "#graphql/supergraphHooks";
import {
  type CreateVaultDocumentInput,
  type CreateVaultDocumentMutation,
  type CreateVaultDocumentMutationVariables,
} from "#graphql/supergraphTypes";

export interface UseCreateVaultDocumentReturn {
  createVaultDocument: (
    input: CreateVaultDocumentInput,
  ) => Promise<CreateVaultDocumentMutation["createVaultDocument"] | null>;
  loading: boolean;
  error: ReturnType<typeof useCreateVaultDocumentMutation>[1]["error"];
}

/**
 * DV-245 — step 1 of upload: create the document row and mint a presigned
 * upload target for the bytes. Returns `{ document, uploadTarget }`, or `null`
 * if the mutation returned no data. Usually driven via {@link useUploadVaultDocument}.
 */
export const useCreateVaultDocument = (
  options?: MutationHookOptions<
    CreateVaultDocumentMutation,
    CreateVaultDocumentMutationVariables
  >,
): UseCreateVaultDocumentReturn => {
  const [mutate, { loading, error }] = useCreateVaultDocumentMutation(options);

  const createVaultDocument = useCallback(
    async (input: CreateVaultDocumentInput) => {
      const { data } = await mutate({ variables: { input } });
      return data?.createVaultDocument ?? null;
    },
    [mutate],
  );

  return { createVaultDocument, loading, error };
};
